/**
 * ===========================================================================
 *  MISSION FLOW — the battle itself
 * ===========================================================================
 *
 * Everything else on this project builds a battlefield; this module turns it
 * into a BATTLE. It owns the opening, the objectives, base capture, faction
 * morale, the wave/escalation ladder, victory, defeat and the results screen.
 *
 * ---------------------------------------------------------------------------
 * THE SHAPE OF A DYNASTY WARRIORS STAGE (reference plate E/F)
 * ---------------------------------------------------------------------------
 *   1. an establishing sweep over two armies already drawn up,
 *   2. an objective announced by name,
 *   3. sites that change hands and move a visible morale tide,
 *   4. named officers arriving in escalating waves — captains, a sub-general,
 *      then the enemy general, whose death ends the stage,
 *   5. a results card with KOs, time, best chain, sites held and a rank.
 * That is the ladder implemented here, as five phases:
 *      muster -> vanguard -> officers -> lieutenant -> general -> over
 *
 * ---------------------------------------------------------------------------
 * HOW IT COOPERATES WITH THE OTHER MODULES
 * ---------------------------------------------------------------------------
 * MORALE is not duplicated. `world.ai.morale` is the single source of truth —
 * the AI moves it on every death and every capture, and grunts read it in
 * `think()` to decide whether to rout. The mission ADDS to that array for
 * objective-scale events and mirrors it into `world.mission.morale` every
 * step, so the HUD tide, the AI and the mission can never disagree.
 * The morale it publishes then feeds back into the field: allied squads are
 * called forward through `world.ai.callToBattle()` in proportion to Wilwatikta
 * morale, and broken Kidul units are pushed toward their rout threshold.
 *
 * SPAWNING is all `world.director` — `spawnOfficer` for named men,
 * `sendWave` for reinforcements, `spawnKnot` for a fresh garrison after a
 * capture, plus `hotspots` retargeting so a captured site starts refilling
 * with ITS NEW OWNER's men instead of the old garrison.
 *
 * THE CAMERA's scripted sweep is triggered by the bus event `'mission:start'`
 * (docs/REQUESTS.md, camera -> mission). It is emitted only by a LOUD start —
 * the real title-screen path, or `mission.start({cinematic:true})`. The
 * headless auto-start is silent so that every existing capture scenario keeps
 * its own framing instead of spending five seconds inside a cinematic.
 *
 * TIMING. All gameplay lives in `fixedUpdate` at a fixed 1/60. `update()` only
 * drives the DOM overlay. Nothing allocates per frame: head-counts go through
 * `entities.countIn`, neighbour queries use one module-scope Int32Array, and
 * every DOM write is behind a last-value cache (see missionhud.js).
 *
 * ---------------------------------------------------------------------------
 * PUBLIC API (world.mission)
 * ---------------------------------------------------------------------------
 *   state        'briefing' | 'active' | 'victory' | 'defeat'
 *   phase        'muster' | 'vanguard' | 'officers' | 'lieutenant' |
 *                'general' | 'over'          (+ phaseIndex, phaseLabel)
 *   bases        capture records — {id,name,x,z,radius,type,team,progress,
 *                capTeam,contested,ally,enemy,...}
 *   objectives   live objective records — {id,kind,text,sub,state,t,limit}
 *   morale       [allyMorale, foeMorale] mirrored from world.ai.morale
 *   tide         0..1, Wilwatikta share of the tide (what the HUD bar shows)
 *   elapsed      seconds of battle
 *   result       null until the battle ends, then the results payload
 *   capture      the live capture meter record, or null
 *   objective    the active primary objective, or null
 *   officers     named men this mission put on the field
 *   stats        { kills, basesTaken, basesLost, waves, objDone, objFailed }
 *   rules        { playerDeath, campFalls, autoStart }
 *   start(opts)  begin the battle    ({cinematic, quiet})
 *   reset(opts)  full deterministic restart
 *   pause() / resume() / advance() / win(reason) / lose(reason)
 *   progressOf(baseId) -> 0..1
 *   info()       compact JSON snapshot for captures
 */
import { ORDER } from '../core/systems.js';
import { TEAM, ST } from '../core/entities.js';
import { CFG } from '../core/config.js';
import { clamp, clamp01 } from '../core/mathx.js';
import { createCaptureBoard } from './capture.js';
import { createMissionHud } from './missionhud.js';
import { ARENA_NAME, STORY, DEFAULT_STAGE } from '../world/layout.js';

/* ------------------------------------------------------------- the script */

/**
 * The escalation ladder. `wave` is the seconds between enemy reinforcement
 * waves, `maxT` the point at which the battle moves on without the player.
 */
const PHASE = [
  { key: 'muster',     label: 'Muster',          music: 'explore',  wave: 0,  pressure: 0.30, maxT: 8 },
  { key: 'vanguard',   label: 'Vanguard',        music: 'skirmish', wave: 26, pressure: 0.46, maxT: 240 },
  { key: 'officers',   label: 'Kidul Captains',  music: 'battle',   wave: 21, pressure: 0.58, maxT: 260 },
  { key: 'lieutenant', label: 'Sub-General',     music: 'battle',   wave: 17, pressure: 0.70, maxT: 300 },
  { key: 'general',    label: 'The General',     music: 'heavy',    wave: 14, pressure: 0.84, maxT: 0 },
  { key: 'over',       label: '',                music: null,       wave: 0,  pressure: 0.30, maxT: 0 },
];

/** The Segara Kidul command. Exported so the front end can name them in a
 *  briefing without a second, drifting copy of the same four. */
export const FOE = {
  captainA: { name: 'Buta Cakil',      title: 'Kidul Captain',         hp: 1000, retinue: 11 },
  captainB: { name: 'Buta Terong',     title: 'Kidul Captain',         hp: 1000, retinue: 11 },
  lieut:    { name: 'Ki Ageng Lodaya', title: 'Kidul Sub-General',     hp: 1600, retinue: 15 },
  general:  { name: 'Ratu Jaladri',    title: 'Queen of Segara Kidul', hp: 2600, retinue: 18, general: true },
};
const ALLY = { name: 'Jayeng Rana', title: 'Wilwatikta Officer', hp: 1100, retinue: 8 };

const END_HOLD = 4.6;          // seconds between the last blow and the results card
const MUSIC_HOLD = 14;         // seconds a scripted music state holds before 'auto'

const _ids = new Int32Array(512);

const mmss = (s) => {
  s = Math.max(0, s | 0);
  const m = (s / 60) | 0, r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
};

/* ========================================================================== */

export function createMission() {
  const board = createCaptureBoard();
  const hud = createMissionHud();

  let W = null;
  let ready = false;            // bases/ui found, board built
  let started = false;
  let phase = 0, phaseT = 0;
  let waveT = 0, pushT = 0, musicT = 0, endT = 0, spawnRetry = 0;
  let bannerHold = 0;      // a silent auto-start keeps the centre banner clear
  let endReason = '';
  let objVer = 0, shownVer = -1;
  let seed0 = 20240819;
  let homeBaseId = null;
  let resultsShown = false;
  let outcomeLocked = false;
  let pending = null;           // a foe spawn waiting for a legal spot

  const objectives = [];
  const officers = [];          // named men this mission spawned
  const stats = { kills: 0, basesTaken: 0, basesLost: 0, waves: 0, objDone: 0, objFailed: 0, flips: 0 };
  const rules = { playerDeath: true, campFalls: true, autoStart: true };
  /**
   * What the chosen battlefield changes. The valley is one authored heightfield
   * and does not move; the STAGE is the light, the landing point, and how hard
   * the Kidul push. `pressure` multiplies the phase ladder's reinforcement rate,
   * `foeHp` the named officers' health — both applied where the script already
   * reads them, so a five-star stage is harder rather than merely labelled so.
   */
  const stage = { id: DEFAULT_STAGE, name: ARENA_NAME, pressure: 1, foeHp: 1, mood: 'golden', focus: null };
  const morale = [0.5, 0.5, 0.5];
  const focusPt = { x: 0, z: 0 };
  const _sec = [];              // scratch: the one side order the plaque shows

  /** The published object. Names here are contract; extend, never rename. */
  const M = {
    version: 1,
    name: ARENA_NAME,
    state: 'briefing',
    phase: PHASE[0].key, phaseIndex: 0, phaseLabel: PHASE[0].label,
    objectives, morale, officers, stats, rules,
    elapsed: 0, tide: 0.5, result: null, paused: false,
    openingPlayed: false,
  };

  /* ------------------------------------------------------------- helpers */

  const log = (t, k) => { W && W.ui && W.ui.log && W.ui.log(t, k); };
  /**
   * Big centre banner. Held down for the first seconds of a SILENT auto-start
   * so the short capture scenarios every other agent relies on (idle, combo,
   * crowd, hud) are not photographed through a mission banner.
   */
  const banner = (t, s, d) => {
    if (bannerHold > 0) return;
    W && W.ui && W.ui.banner && W.ui.banner(t, s, d);
  };

  function music(state, pin) {
    if (!W || !W.audio || !W.audio.music || !state) return;
    W.audio.music(state);
    musicT = pin ? -1 : MUSIC_HOLD;
  }

  /** Move a faction's morale. `world.ai.morale` is the single source of truth. */
  function shift(team, d) {
    const m = W && W.ai && W.ai.morale;
    if (m) m[team] = clamp(m[team] + d, 0.05, 0.95);
    else morale[team] = clamp(morale[team] + d, 0.05, 0.95);
  }
  function mirrorMorale() {
    const m = W && W.ai && W.ai.morale;
    if (m) { morale[0] = m[0]; morale[1] = m[1]; }
    M.tide = morale[0] / Math.max(0.001, morale[0] + morale[1]);
  }

  function isAlive(rec) {
    const e = W.entities;
    return !!rec && rec.id >= 0 && e.alive[rec.id] && e.uid[rec.id] === rec.uid && e.state[rec.id] !== ST.DEAD;
  }

  function baseRec(id) { return board.get(id); }

  function nearestBaseOf(team, x, z) {
    let best = null, bd = 1e18;
    for (let i = 0; i < board.recs.length; i++) {
      const r = board.recs[i];
      if ((r.base.team | 0) !== team) continue;
      const d = (r.x - x) * (r.x - x) + (r.z - z) * (r.z - z);
      if (d < bd) { bd = d; best = r; }
    }
    return best;
  }

  /** Where the battle should converge: the live objective, else the hero. */
  function focus(w) {
    const e = w.entities, pid = w.playerId;
    const o = primary();
    if (o && o.kind === 'capture') {
      const r = baseRec(o.baseId);
      if (r) { focusPt.x = r.x; focusPt.z = r.z; return focusPt; }
    }
    if (o && o.foes && o.foes.length) {
      const f = officers[o.foes[0]];
      if (isAlive(f)) { focusPt.x = e.x[f.id]; focusPt.z = e.z[f.id]; return focusPt; }
    }
    focusPt.x = pid >= 0 ? e.x[pid] : 0;
    focusPt.z = pid >= 0 ? e.z[pid] : 0;
    return focusPt;
  }

  /* --------------------------------------------------------- objectives */

  function addObjective(kind, text, o) {
    const rec = {
      id: (o && o.id) || kind + '_' + objectives.length,
      kind, text, sub: (o && o.sub) || '',
      state: 'active', t: 0, limit: (o && o.limit) || 0,
      baseId: (o && o.baseId) || null,
      foes: (o && o.foes) || null,
      expect: (o && o.expect) || 0,
      primary: !!(o && o.primary),
      reward: o && o.reward !== undefined ? o.reward : 0.06,
      penalty: o && o.penalty !== undefined ? o.penalty : 0.06,
      doneText: (o && o.doneText) || null,
      failText: (o && o.failText) || null,
      holdT: 0,
    };
    objectives.push(rec);
    objVer++;
    if (rec.primary) {
      banner('NEW ORDERS', text, 2.6);
      log('Objective: ' + text, 'warn');
    } else {
      log(text, 'warn');
    }
    return rec;
  }

  /** The newest live order. Older ones are demoted by `setPhase`. */
  function primary() {
    for (let i = objectives.length - 1; i >= 0; i--) {
      const o = objectives[i];
      if (o.primary && o.state === 'active') return o;
    }
    return null;
  }

  function completeObjective(w, o) {
    if (o.state !== 'active') return;
    o.state = 'done'; o.holdT = 0; objVer++;
    stats.objDone++;
    shift(TEAM.PLAYER, o.reward);
    shift(TEAM.ENEMY, -o.reward * 0.8);
    log(o.doneText || (o.text + ' — done'), 'good');
    if (o.primary) {
      banner('OBJECTIVE COMPLETE', o.text, 2.4);
      w.audio && w.audio.play && w.audio.play('ko_chime', { gain: 0.5 });
      if (phase < PHASE.length - 1) setPhase(w, phase + 1);
    }
  }

  function failObjective(w, o) {
    if (o.state !== 'active') return;
    o.state = 'failed'; o.holdT = 0; objVer++;
    stats.objFailed++;
    shift(TEAM.PLAYER, -o.penalty);
    shift(TEAM.ENEMY, o.penalty * 0.8);
    log(o.failText || (o.text + ' — failed'), 'bad');
    if (o.primary) banner('OBJECTIVE FAILED', o.text, 2.4);
  }

  function updateObjectives(w, dt) {
    for (let i = objectives.length - 1; i >= 0; i--) {
      const o = objectives[i];
      if (o.state !== 'active') {
        o.holdT += dt;
        if (o.holdT > 10 && !o.primary) { objectives.splice(i, 1); objVer++; }
        continue;
      }
      o.t += dt;
      switch (o.kind) {
        case 'capture': {
          const r = baseRec(o.baseId);
          if (r && (r.base.team | 0) === TEAM.PLAYER) completeObjective(w, o);
          break;
        }
        case 'defeat': {
          if (!o.foes || o.foes.length < Math.max(1, o.expect)) break;
          let all = true;
          for (let k = 0; k < o.foes.length; k++) if (isAlive(officers[o.foes[k]])) { all = false; break; }
          if (all) completeObjective(w, o);
          break;
        }
        case 'defend': {
          const r = baseRec(o.baseId);
          if (r && (r.base.team | 0) !== TEAM.PLAYER) failObjective(w, o);
          else if (o.limit && o.t >= o.limit) completeObjective(w, o);
          break;
        }
        case 'protect': {
          const f = officers[o.foes[0]];
          if (!isAlive(f)) failObjective(w, o);
          else if (o.limit && o.t >= o.limit) completeObjective(w, o);
          break;
        }
        default:
          if (o.limit && o.t >= o.limit) completeObjective(w, o);
      }
    }
  }

  /* ------------------------------------------------------- named officers */

  /**
   * Put a named man on the field. He marches in from his own side's nearest
   * holding, so nobody ever pops into view. Returns the officer record or
   * null; a failed placement is retried on a later step.
   */
  function spawnNamed(w, spec, team) {
    const d = w.director, e = w.entities, pid = w.playerId;
    if (!d || !d.spawnOfficer || pid < 0) return null;
    const ORDERS = w.ai && w.ai.ORDERS ? w.ai.ORDERS : null;
    const FORM = w.ai && w.ai.FORM ? w.ai.FORM : null;
    const px = e.x[pid], pz = e.z[pid];
    const home = nearestBaseOf(team, px, pz);
    const rng = w.rng;

    // his own holding if it is close enough to march from, otherwise a point
    // 46-68 m off the hero on the bearing of his own lines — near enough to
    // reach the fight, far enough that nothing pops into view
    const hd = home ? Math.hypot(home.x - px, home.z - pz) : 1e9;
    for (let k = 0; k < 8; k++) {
      let sx, sz;
      if (home && hd < 95 && k < 3) {
        const a = rng.next() * 6.2832;
        sx = home.x + Math.sin(a) * (home.radius + 6);
        sz = home.z + Math.cos(a) * (home.radius + 6);
        if ((sx - px) * (sx - px) + (sz - pz) * (sz - pz) < 26 * 26) continue;
      } else {
        let ax, az;
        if (home) { ax = home.x - px; az = home.z - pz; const l = Math.hypot(ax, az) || 1; ax /= l; az /= l; }
        else { const a = rng.next() * 6.2832; ax = Math.sin(a); az = Math.cos(a); }
        const sp = (rng.next() - 0.5) * 1.7;
        const ca = Math.cos(sp), sa = Math.sin(sp);
        const bx = ax * ca - az * sa, bz = ax * sa + az * ca;
        const r = 46 + rng.next() * 22;
        sx = px + bx * r; sz = pz + bz * r;
      }
      if (sx < -230 || sx > 230 || sz < -230 || sz > 230) continue;
      const id = d.spawnOfficer(team, sx, sz, spec.retinue || 10, {
        form: FORM ? FORM.WEDGE : 0,
        order: ORDERS ? ORDERS.ADVANCE : 0,
        gx: px, gz: pz, fx: (px - sx), fz: (pz - sz),
        speed: 3.0, spacing: 1.9, sd: 2.4,
        name: spec.name, general: !!spec.general,
      });
      if (id < 0) continue;
      if (spec.hp) {
        e.hp[id] = e.maxHp[id] = Math.round(spec.hp * stage.foeHp);
        e.maxPoise[id] = e.poise[id] = spec.general ? 110 : 75;
      }
      const rec = e.ext[id];
      if (rec) {
        if (spec.title) rec.title = spec.title;
        rec.maxHp = e.maxHp[id]; rec.hp = e.hp[id];
      }
      const entry = {
        id, uid: e.uid[id], name: spec.name, title: spec.title || '',
        team, general: !!spec.general, from: home ? home.name : 'the field',
        x: sx, z: sz,
      };
      officers.push(entry);
      return entry;
    }
    return null;
  }

  /** Queue a named man; retried each second until the terrain accepts him. */
  function requestFoe(spec, team, then) {
    pending = { spec, team, then, t: 0 };
  }
  function stepPending(w, dt) {
    if (!pending) return;
    pending.t -= dt;
    if (pending.t > 0) return;
    pending.t = 1.0;
    const rec = spawnNamed(w, pending.spec, pending.team);
    if (!rec) { spawnRetry++; if (spawnRetry > 30) pending = null; return; }
    spawnRetry = 0;
    const then = pending.then; pending = null;
    if (then) then(w, rec, officers.length - 1);
  }

  /* -------------------------------------------------------------- phases */

  function setPhase(w, i, opts) {
    phase = clamp(i | 0, 0, PHASE.length - 1);
    phaseT = 0;
    const P = PHASE[phase];
    M.phase = P.key; M.phaseIndex = phase; M.phaseLabel = P.label;
    waveT = P.wave ? P.wave * 0.55 : 0;
    if (w.director) w.director.pressure = clamp01(P.pressure * stage.pressure);
    if (P.music) music(P.music, false);
    // An order the battle has moved past (a phase that timed out, or a forced
    // advance) stays live and can still be won — it just stops being THE
    // objective, so the plaque always reads the newest command.
    for (let k = 0; k < objectives.length; k++) {
      const o = objectives[k];
      if (o.primary && o.state === 'active') { o.primary = false; o.demoted = true; }
    }
    objVer++;
    const quiet = !!(opts && opts.quiet);
    switch (P.key) {
      case 'muster': openMuster(w, quiet); break;
      case 'vanguard': openVanguard(w); break;
      case 'officers': openOfficers(w); break;
      case 'lieutenant': openLieutenant(w); break;
      case 'general': openGeneral(w); break;
      default: break;
    }
  }

  function openMuster(w, quiet) {
    const goal = crossing();
    log(STORY.landing, 'warn');
    log(goal ? 'Wilwatikta vanguard — form up and march on ' + goal.name
             : 'Wilwatikta vanguard — ' + STORY.holdLog, '');
    if (!quiet) banner(stage.name.toUpperCase(), 'Wilwatikta vanguard, ' + STORY.hold, 3.2);
    addObjective('hold', 'Form the vanguard', {
      sub: goal ? goal.name + ' lies ahead' : 'The vanguard forms up',
      limit: PHASE[0].maxT, primary: true, reward: 0.02,
      doneText: 'The vanguard is formed',
    });
  }

  function openVanguard(w) {
    const b = crossing();
    if (b) {
      addObjective('capture', 'Seize ' + b.name, {
        baseId: b.id, sub: 'Clear the Kidul garrison and hold the ground', primary: true, reward: 0.10,
        doneText: b.name + ' is ours',
      });
      // our own men move up with us
      w.ai && w.ai.callToBattle && w.ai.callToBattle(b.x, b.z, TEAM.PLAYER, 200, 3);
      w.director && w.director.sendWave && w.director.sendWave(b.x, b.z, TEAM.PLAYER, 10);
    }
    // the west gate holds our flank
    const west = westGate();
    if (west) {
      addObjective('defend', 'Defend ' + west.name, {
        baseId: west.id, sub: 'A Kidul column is moving on our flank', limit: 110, reward: 0.07, penalty: 0.09,
        doneText: west.name + ' still flies our banner',
        failText: west.name + ' has fallen to Segara Kidul',
      });
      w.director && w.director.sendWave && w.director.sendWave(west.x, west.z, TEAM.ENEMY, 10);
    }
  }

  function openOfficers(w) {
    banner('KIDUL CAPTAINS', 'Two officers ride out', 2.8);
    log(FOE.captainA.name + ' and ' + FOE.captainB.name + ' have taken the field', 'warn');
    const o = addObjective('defeat', 'Defeat the Kidul captains', {
      sub: FOE.captainA.name + ' · ' + FOE.captainB.name, primary: true, reward: 0.10, foes: [], expect: 2,
      doneText: 'Both Kidul captains have fallen',
    });
    requestFoe(FOE.captainA, TEAM.ENEMY, (ww, rec, idx) => {
      o.foes.push(idx); objVer++;
      requestFoe(FOE.captainB, TEAM.ENEMY, (w2, r2, i2) => { o.foes.push(i2); objVer++; });
    });
  }

  function openLieutenant(w) {
    banner(FOE.lieut.name.toUpperCase(), FOE.lieut.title, 3.0);
    log('Sub-general ' + FOE.lieut.name + ' has appeared at the head of the Kidul column', 'warn');
    const o = addObjective('defeat', 'Defeat ' + FOE.lieut.name, {
      sub: FOE.lieut.title, primary: true, reward: 0.12, foes: [], expect: 1,
      doneText: FOE.lieut.name + ' has been cut down',
    });
    requestFoe(FOE.lieut, TEAM.ENEMY, (ww, rec, idx) => { o.foes.push(idx); objVer++; });
    // and an ally who needs help — success and failure both cost something
    const ally = spawnNamed(w, ALLY, TEAM.PLAYER);
    if (ally) {
      const idx = officers.length - 1;
      addObjective('protect', ALLY.name + ' is in danger', {
        sub: 'Keep them alive', limit: 80, foes: [idx], reward: 0.08, penalty: 0.10,
        doneText: ALLY.name + ' holds their ground',
        failText: ALLY.name + ' has fallen — the Wilwatikta line wavers',
      });
      const e = w.entities;
      w.director && w.director.sendWave && w.director.sendWave(e.x[ally.id], e.z[ally.id], TEAM.ENEMY, 12);
    }
  }

  function openGeneral(w) {
    banner(FOE.general.name.toUpperCase(), FOE.general.title, 3.4);
    log(FOE.general.name + ' takes the field in person', 'warn');
    log('Cut the Kidul general down and the host breaks', '');
    const o = addObjective('defeat', 'Defeat ' + FOE.general.name, {
      sub: FOE.general.title, primary: true, reward: 0.16, foes: [], expect: 1,
      // NOT "<name> has fallen": `src/ai/ai.js onDeath()` already toasts exactly
      // that string for every named man, and the two landed in the mission log
      // as a literal duplicate line.
      doneText: 'The Kidul host is leaderless',
    });
    requestFoe(FOE.general, TEAM.ENEMY, (ww, rec, idx) => { o.foes.push(idx); objVer++; });
    music('heavy', false);
  }

  /** The neutral crossing is the opening prize; fall back to any site not ours. */
  /**
   * The holding the vanguard is sent to take. The chosen battlefield nominates
   * it (`stage.focus`) — a player who picked Gapura Wringin and is then
   * ordered to seize the bridge two hundred metres behind him has been told
   * the stage select was decoration. Falls back to the authored bridge.
   */
  function crossing() {
    if (stage.focus) {
      const f = board.get(stage.focus);
      if (f && (f.base.team | 0) !== TEAM.PLAYER) return f;
    }
    let best = null;
    for (let i = 0; i < board.recs.length; i++) {
      const r = board.recs[i];
      if ((r.base.team | 0) === TEAM.PLAYER) continue;
      if (r.type === 'bridge') return r;
      if (!best || r.type !== 'fort') best = r;
    }
    return best;
  }
  function westGate() {
    for (let i = 0; i < board.recs.length; i++) {
      const r = board.recs[i];
      if ((r.base.team | 0) === TEAM.PLAYER && r.type === 'camp') return r;
    }
    return null;
  }

  /* --------------------------------------------------------------- waves */

  function waves(w, dt) {
    const P = PHASE[phase];
    if (!P.wave || M.state !== 'active') return;
    waveT -= dt;
    if (waveT > 0) return;
    waveT = P.wave * (0.8 + w.rng.next() * 0.5);
    const f = focus(w);
    const n = 8 + phase * 2;
    if (w.director && w.director.sendWave) {
      if (w.director.sendWave(f.x, f.z, TEAM.ENEMY, n) >= 0) stats.waves++;
      // allies answer when their blood is up
      if (morale[0] > 0.52 && w.rng.next() < (morale[0] - 0.42) * 2) {
        w.director.sendWave(f.x, f.z, TEAM.PLAYER, 6 + phase);
      }
    }
  }

  /**
   * Morale made visible. High Wilwatikta morale calls allied squads forward onto
   * the objective; low Kidul morale pushes their men over the rout threshold that
   * `src/ai/grunt.js think()` tests against `world.ai.morale`.
   */
  function moraleEffects(w, dt) {
    pushT -= dt;
    if (pushT > 0) return;
    pushT = 2.0;
    const e = w.entities, pid = w.playerId;
    const f = focus(w);
    const ally = Math.round(clamp01((morale[0] - 0.42) / 0.38) * 3);
    if (ally > 0 && w.ai && w.ai.callToBattle) w.ai.callToBattle(f.x, f.z, TEAM.PLAYER, 170, ally);
    if (pid >= 0 && w.ai && w.ai.callToBattle) {
      const foe = Math.round(clamp01((morale[1] - 0.45) / 0.38) * 2);
      if (foe > 0) w.ai.callToBattle(e.x[pid], e.z[pid], TEAM.ENEMY, 150, foe);
    }
    if (morale[1] < 0.30 && pid >= 0) {
      const drain = (0.30 - morale[1]) * 0.22;
      const n = e.around(e.x[pid], e.z[pid], 42, _ids, _ids.length);
      for (let i = 0; i < n; i++) {
        const id = _ids[i];
        if (!e.alive[id] || e.team[id] !== TEAM.ENEMY || e.state[id] === ST.DEAD) continue;
        e.morale[id] = Math.max(0, e.morale[id] - drain);
      }
    }
  }

  /* ------------------------------------------------------- base captures */

  function onFlip(w, r, team) {
    stats.flips++;
    const e = w.entities;
    const ours = team === TEAM.PLAYER;
    if (ours) stats.basesTaken++; else stats.basesLost++;

    // 1. the site now reinforces its new owner (the director refills hotspots)
    const hs = w.director && w.director.hotspots;
    if (hs) {
      const rr = (r.radius + 20) * (r.radius + 20);
      for (let i = 0; i < hs.length; i++) {
        const h = hs[i];
        if (h.team < 0) continue;
        const dx = h.x - r.x, dz = h.z - r.z;
        if (dx * dx + dz * dz <= rr) h.team = team;
      }
    }
    // 2. a fresh garrison forms on the site's own spawn points
    if (w.director && w.director.spawnKnot) {
      const s = r.spawns.length ? r.spawns[stats.flips % r.spawns.length] : null;
      const ORDERS = w.ai && w.ai.ORDERS ? w.ai.ORDERS : null;
      const FORM = w.ai && w.ai.FORM ? w.ai.FORM : null;
      w.director.spawnKnot(team, s ? s.x : r.x, s ? s.z : r.z, 10, {
        form: FORM ? FORM.BLOB : 0, order: ORDERS ? ORDERS.HOLD : 0,
        gx: r.x, gz: r.z, sd: 2.6, baseId: r.id,
      });
    }
    // 3. morale — the AI's own 'base:captured' handler moves the tide; a major
    //    site is worth more than an outpost, so add the difference here.
    const major = r.type === 'fort' || r.type === 'camp' ? 0.05 : 0.02;
    shift(team, major);
    shift(team === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER, -major);

    // 4. the men who just lost the ground see it happen
    const loser = ours ? TEAM.ENEMY : TEAM.PLAYER;
    const n = e.around(r.x, r.z, r.radius + 14, _ids, _ids.length);
    for (let i = 0; i < n; i++) {
      const id = _ids[i];
      if (!e.alive[id] || e.state[id] === ST.DEAD || id === w.playerId) continue;
      if (e.team[id] === loser) e.morale[id] = Math.max(0, e.morale[id] - 0.14);
      else e.morale[id] = Math.min(1, e.morale[id] + 0.06);
    }

    // 5. sound + a short camera beat when the hero took it himself
    if (w.audio && w.audio.play) {
      w.audio.play(r.type === 'fort' ? 'gate_open' : 'war_horn', { x: r.x, y: (r.base.y || 0) + 3, z: r.z, gain: 0.9 });
    }
    if (r.hero && w.camrig && w.camrig.cinematic) w.camrig.cinematic('duel');
    log(ours ? ('The Kidul garrison at ' + r.name + ' breaks') : (r.name + ' has been overrun'), ours ? 'good' : 'bad');

  }

  function onContest(w, r, team) {
    if (r.logT > 0) return;
    r.logT = 20;
    if (team === TEAM.PLAYER) log('Our men are pressing into ' + r.name, '');
    else log(r.name + ' is under attack', 'warn');
  }

  /* ------------------------------------------------------- end of battle */

  function win(reason) {
    const w = W;
    if (outcomeLocked || M.state !== 'active') return M.state;
    outcomeLocked = true;
    M.state = 'victory'; endT = 0; resultsShown = false;
    endReason = reason || 'general'; bannerHold = 0;
    w.flags.godMode = true;
    banner('VICTORY', reason === 'rout' ? STORY.won : 'The Kidul banner falls', 4.0);
    log('The Kidul host is broken', 'good');
    music('victory', true);
    w.bus.emit('mission:end', 'victory', reason);
    return M.state;
  }

  function lose(reason) {
    const w = W;
    if (outcomeLocked || M.state !== 'active') return M.state;
    outcomeLocked = true;
    M.state = 'defeat'; endT = 0; resultsShown = false;
    endReason = reason || 'lost'; bannerHold = 0;
    w.flags.godMode = true;
    banner('DEFEAT', reason === 'camp' ? 'Our camp has fallen' : 'The vanguard is broken', 4.0);
    log(reason === 'camp' ? 'Benteng Trowulan has fallen — the army is in rout' : 'The vanguard has fallen', 'bad');
    music('defeat', true);
    w.bus.emit('mission:end', 'defeat', reason);
    return M.state;
  }

  function buildResult(w) {
    const p = w.player;
    const kills = p ? p.kills | 0 : 0;
    const chain = p ? p.maxChain | 0 : 0;
    stats.kills = kills;
    const held = board.held(TEAM.PLAYER);
    const won = M.state === 'victory';

    /* Winning used to be most of the rank. A flat 260 for the win plus a time
     * bonus of clamp(200 - elapsed*0.5) handed out 460 of the 640 an S needed
     * before the player had done anything specific to the stage, so a run that
     * finished with 31 KOs, ZERO bases taken and five of seven objectives
     * unresolved printed S / Peerless directly beside 00:08 FIELD TIME on the
     * results card. See the mission r6 verdict in docs/PROGRESS.jsonl.
     *
     * Two changes. The win is worth less on its own (120), and the speed bonus
     * is EARNED rather than granted: it scales with how much of the stage was
     * actually taken, so finishing fast is only worth points if you finished a
     * fast version of the whole battle rather than decapitating it. A stage
     * won in eight seconds having captured nothing now scores its 31 kills,
     * its chain and its two objectives, and lands where that belongs. */
    const scope = clamp((stats.basesTaken + stats.objDone) / 6, 0, 1);
    const timeBonus = won ? clamp(200 - M.elapsed * 0.5, 0, 200) * scope : 0;
    const score = kills + chain * 2.2 + stats.basesTaken * 55 + stats.objDone * 35 +
      (won ? 120 : 0) - stats.objFailed * 40 - stats.basesLost * 45 + timeBonus;
    const rank = !won ? (score >= 420 ? 'C' : 'D')
      : score >= 640 ? 'S' : score >= 470 ? 'A' : score >= 300 ? 'B' : 'C';
    const note = { S: 'Peerless', A: 'Renowned', B: 'Seasoned', C: 'Blooded', D: 'Survived' }[rank];
    M.result = {
      outcome: M.state, reason: endReason, phase: M.phase, kills, maxChain: chain,
      bases: held, basesTaken: stats.basesTaken, basesLost: stats.basesLost,
      seconds: +M.elapsed.toFixed(2), time: mmss(M.elapsed),
      objectives: stats.objDone, failed: stats.objFailed,
      morale: [+morale[0].toFixed(3), +morale[1].toFixed(3)],
      score: Math.round(score), rank, note,
    };
    return M.result;
  }

  function showResults(w) {
    const r = buildResult(w);
    if (w.ui && w.ui.showResults) {
      w.ui.showResults({
        title: M.state === 'victory' ? 'VICTORY' : 'DEFEAT',
        subtitle: M.name, kills: r.kills, maxChain: r.maxChain,
        bases: r.bases, time: r.time, seconds: r.seconds, rank: r.rank, note: r.note,
      });
    }
    resultsShown = true;
    return r;
  }

  /* --------------------------------------------------------- life cycle */

  function start(opts) {
    const w = W;
    if (!w || started) return M;
    const o = opts || {};
    started = true;
    rules.autoStart = true;
    bannerHold = o.quiet ? 12 : 0;
    outcomeLocked = false;
    M.state = 'active'; M.elapsed = 0; M.result = null; resultsShown = false;
    endT = 0; objectives.length = 0; objVer++;
    stats.kills = 0;
    w.flags.godMode = false;
    w.player?.setAllowDeath?.(rules.playerDeath);
    if (w.ai) w.ai.allowPlayerDeath = rules.playerDeath;
    setPhase(w, 0, o);
    // The camera's establishing sweep listens for this (docs/REQUESTS.md).
    // Only a LOUD start emits it: a silent auto-start must not put every
    // capture scenario inside a five-second cinematic.
    if (o.cinematic) { M.openingPlayed = true; w.bus.emit('mission:start', M); }
    return M;
  }

  function clearField(w, battleSeed) {
    const e = w.entities;
    for (let i = e.liveN - 1; i >= 0; i--) {
      const id = e.live[i];
      if (id === w.playerId || !e.alive[id]) continue;
      w.ai && w.ai.discharge && w.ai.discharge(id);
    }
    if (w.ai && w.ai.officers) w.ai.officers.length = 0;
    e.reset(w.playerId, battleSeed);
  }

  /** A full, deterministic restart: same seed, same authored battle. */
  function reset(opts) {
    const w = W;
    if (!w) return M;
    const o = opts || {};
    const battleSeed = o.seed !== undefined ? o.seed : seed0;

    // Publish a non-playable state before bases/entities are reconstructed:
    // restoring an authored banner emits base events synchronously.
    M.state = 'resetting'; M.paused = false;
    started = false; outcomeLocked = true; resultsShown = false;
    rules.autoStart = false;
    w.flags.godMode = true; w.flags.paused = false;

    w.engine?.resetBattleTime?.();
    w.combat?.resetBattle?.();
    w.rng.seed(battleSeed);
    clearField(w, battleSeed);
    w.ai?.resetBattle?.();
    w.player?.resetBattle?.();
    w.entities.refresh();
    w.anim?.resetBattle?.();
    w.director?.resetBattle?.();
    board.reset(w);

    officers.length = 0; objectives.length = 0; pending = null; spawnRetry = 0;
    stats.kills = 0; stats.basesTaken = 0; stats.basesLost = 0; stats.waves = 0;
    stats.objDone = 0; stats.objFailed = 0; stats.flips = 0;
    phase = 0; phaseT = 0; waveT = 0; pushT = 0; musicT = 0; endT = 0; bannerHold = 0;
    objVer++; shownVer = -1;
    endReason = '';
    M.state = 'briefing'; M.elapsed = 0; M.result = null; M.paused = false;
    M.phase = PHASE[0].key; M.phaseIndex = 0; M.phaseLabel = PHASE[0].label;
    M.openingPlayed = false;
    if (w.ai) {
      w.ai.morale[0] = 0.5; w.ai.morale[1] = 0.5; w.ai.engaged = null;
      w.ai.allowPlayerDeath = rules.playerDeath;
      // Compatibility seam for an older AI module without resetBattle().
      if (!w.ai.resetBattle) {
        w.ai.store?.uid?.fill(0);
        w.ai.store?.grantT?.fill(0); w.ai.store?.tokN?.fill(0); w.ai.store?.ringN?.fill(0);
        w.ai.store?.ringSlot?.fill(0);
      }
    }
    // Compatibility seam: current animation owns resetBattle(). UIDs remain
    // globally monotonic; spawnKey owns deterministic presentation choices.
    if (!w.anim?.resetBattle) {
      w.anim?.state?.uid?.fill(0);
      w.anim?.state?.spawnKey?.fill(0);
    }
    w.player?.setAllowDeath?.(rules.playerDeath);
    mirrorMorale();
    w.flags.godMode = false;
    outcomeLocked = false;
    w.input?.clearBuffer?.();
    w.vfx?.clear?.();
    hud.clear();
    if (w.ui) { w.ui.clear && w.ui.clear(); w.ui.setState && w.ui.setState('playing'); }
    w.camrig?.reset?.();
    music('explore', false);
    // `reset({start:false})` hands the opening to the caller — the headless
    // auto-start must not race it.
    rules.autoStart = o.start !== false;
    if (rules.autoStart) start(o);
    return M;
  }

  function pause() {
    const w = W; if (!w) return false;
    M.paused = true; w.flags.paused = true;
    if (w.ui && w.ui.setState) w.ui.setState('pause');
    return true;
  }
  function resume() {
    const w = W; if (!w) return false;
    M.paused = false; w.flags.paused = false;
    if (w.ui && w.ui.setState) w.ui.setState('playing');
    return true;
  }

  /* -------------------------------------------------------------- system */

  function ensure(w) {
    if (ready) return;
    if (!w.bases || !w.bases.list || !w.bases.list.length) return;
    board.build(w);
    for (let i = 0; i < board.recs.length; i++) board.recs[i].logT = 0;
    board.onFlip = onFlip;
    board.onContest = onContest;
    // the ally main camp: losing it loses the battle
    for (let i = 0; i < board.recs.length; i++) {
      const r = board.recs[i];
      if (r.type === 'fort' && r.team0 === TEAM.PLAYER) { homeBaseId = r.id; break; }
    }
    hud.mount(w);
    mirrorMorale();
    ready = true;
  }

  return {
    name: 'mission', order: ORDER.MISSION,

    init(w) {
      W = w;
      try {
        const p = new URLSearchParams(location.search);
        if (p.has('seed')) seed0 = parseInt(p.get('seed'), 10) || seed0;
      } catch (err) { /* no URL in this host */ }

      /* `world.bases` publishes its list into `world.mission.bases` during ITS
       * init, which runs after ours. Absorb that write and keep serving the
       * mission's own richer capture records under the same name. */
      let rawList = null;
      Object.defineProperty(M, 'bases', {
        configurable: true, enumerable: true,
        get() { return board.recs.length ? board.recs : (rawList || board.recs); },
        set(v) { rawList = v; },
      });
      Object.defineProperty(M, 'capture', { configurable: true, enumerable: true, get: () => board.leader() });
      Object.defineProperty(M, 'objective', { configurable: true, enumerable: true, get: primary });

      /**
       * Commit the battlefield the front end picked. Must land BEFORE the UI
       * reports 'playing', because that is the frame `start()` fires on.
       * Only the knobs a running world can honour are taken; the terrain is
       * carved at boot and this never pretends to move it.
       */
      M.setArena = (a) => {
        if (!a) return stage;
        stage.id = a.id || stage.id;
        stage.name = a.name || stage.name;
        stage.mood = a.mood || stage.mood;
        stage.pressure = a.pressure > 0 ? a.pressure : 1;
        stage.foeHp = a.foeHp > 0 ? a.foeHp : 1;
        stage.focus = a.focus || null;
        M.name = stage.name;
        M.arena = stage;
        if (a.seed !== undefined) seed0 = a.seed | 0;
        return stage;
      };
      M.arena = stage;
      M.start = (o) => start(o || {});
      M.reset = (o) => reset(o || {});
      M.pause = pause;
      M.resume = resume;
      M.win = (r) => win(r || 'debug');
      M.lose = (r) => lose(r || 'debug');
      M.advance = () => { if (phase < PHASE.length - 1) setPhase(W, phase + 1); return M.phase; };
      M.progressOf = (id) => board.progressOf(id);
      M.held = (team) => board.held(team === undefined ? TEAM.PLAYER : team);
      M.setPlayerDeath = (on) => {
        rules.playerDeath = !!on;
        W.player?.setAllowDeath?.(rules.playerDeath);
        // Compatibility setter delegates back to player in the current AI.
        if (W.ai) W.ai.allowPlayerDeath = rules.playerDeath;
        return rules.playerDeath;
      };
      M.info = () => ({
        state: M.state, phase: M.phase, elapsed: +M.elapsed.toFixed(2),
        morale: [+morale[0].toFixed(3), +morale[1].toFixed(3)], tide: +M.tide.toFixed(3),
        kills: w.player ? w.player.kills : 0, maxChain: w.player ? w.player.maxChain : 0,
        held: board.held(TEAM.PLAYER), stats: { ...stats },
        objectives: objectives.map(o => ({ id: o.id, text: o.text, kind: o.kind, state: o.state, primary: o.primary })),
        officers: officers.map(o => ({ name: o.name, team: o.team, alive: isAlive(o) })),
        bases: board.recs.map(r => ({ id: r.id, team: r.base.team | 0, p: +r.progress.toFixed(3), ally: r.ally, foe: r.enemy })),
        result: M.result,
      });

      w.mission = M;

      /* ---- world hooks ------------------------------------------------- */
      /* Losing the main camp loses the battle, taking every site wins it —
       * on the BUS, not just off our own capture meter, so it still holds if
       * anything else ever flips a banner through `bases.setTeam()`. */
      w.bus.on('base:captured', (b) => {
        if (!b || M.state !== 'active' || !ready) return;
        const t = b.team | 0;
        if (t !== TEAM.PLAYER && rules.campFalls && b.id === homeBaseId) lose('camp');
        else if (t === TEAM.PLAYER && board.recs.length && board.held(TEAM.PLAYER) === board.recs.length) win('rout');
      });

      // Combat announces death synchronously. Mission owns the one-shot result;
      // combat keeps the stable corpse entity for camera/HUD presentation.
      w.bus.on('kill', (id) => {
        if (rules.playerDeath && id === w.playerId) lose('fallen');
      });

      w.bus.on('officer:down', (id) => {
        if (M.state !== 'active') return;
        const e = w.entities;
        for (let i = 0; i < officers.length; i++) {
          const o = officers[i];
          if (o.id !== id || !e.alive[id] || e.uid[id] !== o.uid) continue;
          if (o.team === TEAM.ENEMY) {
            shift(TEAM.PLAYER, o.general ? 0.14 : 0.05);
            shift(TEAM.ENEMY, o.general ? -0.20 : -0.06);
            if (o.general) {
              const po = primary();
              if (po && po.kind === 'defeat') completeObjective(w, po);
              win('general');
            }
          } else {
            shift(TEAM.PLAYER, -0.08);
            shift(TEAM.ENEMY, 0.05);
          }
        }
      });

      // Scenario hooks, so captures drive the battle without reaching inside.
      const dw = window.__DW;
      if (dw && dw.scenarios) {
        // NB every hook returns PLAIN DATA. `world.mission` itself must never
        // cross the CDP boundary: its `bases` reach the THREE scene graph and
        // Playwright would try to serialise the whole world.
        dw.scenarios.missionStart = (wd, o) => { wd.mission.start(o || { cinematic: true }); return wd.mission.state; };
        dw.scenarios.missionReset = (wd, o) => { wd.mission.reset(o || {}); return wd.mission.state; };
        dw.scenarios.missionAdvance = (wd) => wd.mission.advance();
        dw.scenarios.missionInfo = (wd) => wd.mission.info();
        dw.scenarios.missionWin = (wd) => wd.mission.win('debug');
      }
    },

    fixedUpdate(w, dt) {
      W = w;
      ensure(w);
      mirrorMorale();
      if (!ready) return;

      if (M.state === 'briefing') {
        if (rules.autoStart && !started) {
          if (CFG.debug.headless) start({ cinematic: false, quiet: true });
          else if (w.ui && w.ui.state === 'playing') start({ cinematic: true });
        }
        // If start() fired on this step, let the new battle own the whole
        // fixed tick. Otherwise a fresh boot begins one tick behind a retry.
        if (M.state === 'briefing') return;
      }
      if (M.paused || w.flags.paused) return;

      if (bannerHold > 0) bannerHold -= dt;
      if (musicT > 0) { musicT -= dt; if (musicT <= 0) music('auto', false); }

      if (M.state !== 'active') {
        endT += dt;
        if (!resultsShown && endT >= END_HOLD) showResults(w);
        return;
      }

      M.elapsed += dt;
      phaseT += dt;
      stats.kills = w.player ? w.player.kills | 0 : 0;

      for (let i = 0; i < board.recs.length; i++) if (board.recs[i].logT > 0) board.recs[i].logT -= dt;
      board.update(w, dt);
      stepPending(w, dt);
      updateObjectives(w, dt);
      waves(w, dt);
      moraleEffects(w, dt);

      const P = PHASE[phase];
      if (P.maxT && phaseT >= P.maxT && phase < PHASE.length - 1) {
        const o = primary();
        if (o && o.kind === 'hold') completeObjective(w, o);
        else setPhase(w, phase + 1);
      }

      /* Direct health mutations and lethal guard chip do not necessarily emit
       * `kill`; close that seam without reviving or rewriting the corpse. */
      if (rules.playerDeath) {
        const e = w.entities, id = w.playerId;
        if (id >= 0 && (e.state[id] === ST.DEAD || e.hp[id] <= 0)) {
          if (e.state[id] !== ST.DEAD) w.player?.die?.(-1);
          else lose('fallen');
        }
      }
    },

    /** Presentation only: the objective plaque and the capture meter. */
    update(w, dt, alpha, realDt) {
      if (!ready) return;
      if (!hud.mounted && !hud.mount(w)) return;
      if (objVer !== shownVer) {
        shownVer = objVer;
        // ONE side order on the plaque. The HUD's own mission log starts at
        // 112u and this plaque hangs from 28u, so a growing list would run
        // straight into the newest log line; everything else is announced
        // there anyway. A live timer (defend / protect) outranks the rest,
        // then the newest order, then a result worth showing for a beat.
        let pick = null, rank = -1;
        for (let i = 0; i < objectives.length; i++) {
          const o = objectives[i];
          if (o.primary) continue;
          const r = o.state !== 'active' ? (o.holdT < 6 ? 1 : 0)
            : (o.kind === 'defend' || o.kind === 'protect') ? 3 : 2;
          if (r > rank || (r === rank && r >= 2)) { rank = r; pick = o; }
        }
        _sec[0] = pick;
        _sec.length = pick ? 1 : 0;
        hud.setObjective(primary(), _sec, M.phaseLabel);
      }
      // never over a modal: the menus layer was appended to the HUD root
      // before ours, so an open pause/results scrim would sit UNDER us
      const uiState = w.ui ? w.ui.state : 'playing';
      const show = M.state === 'active' && !M.paused && uiState === 'playing' && !!primary();
      hud.frame(w, realDt, show, show ? board.leader() : null);
    },

    dispose() { hud.dispose(); },
  };
}
